<template>
  <div class="data-table">
    <div v-if="measurements.length === 0" class="no-data">
      <p>Aucune mesure enregistrée pour cet utilisateur.</p>
    </div>
    
    <div v-else class="table-container">
      <div class="table-header">
        <h3>{{ measurements.length }} mesure{{ measurements.length > 1 ? 's' : '' }} enregistrée{{ measurements.length > 1 ? 's' : '' }}</h3>
        <div class="legend">
          <div class="legend-item">
            <span class="status-indicator status-faible"></span>
            <span>Faible</span>
          </div>
          <div class="legend-item">
            <span class="status-indicator status-standard"></span>
            <span>Standard</span>
          </div>
          <div class="legend-item">
            <span class="status-indicator status-excellent"></span>
            <span>Excellent</span>
          </div>
          <div class="legend-item">
            <span class="status-indicator status-eleve"></span>
            <span>Élevé</span>
          </div>
          <div class="legend-item">
            <span class="status-indicator status-trop-eleve"></span>
            <span>Trop élevé</span>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="measurements-table">
          <thead>
            <tr>
              <th class="metric-header">Métrique</th>
              <th 
                v-for="measurement in measurements" 
                :key="measurement.id"
                class="date-header"
              >
                <div class="datetime">
                  <div class="date">{{ measurement.date }}</div>
                  <div class="time">{{ measurement.time }}</div>
                </div>
                <div class="header-actions">
                  <button 
                    v-if="editingId === measurement.id" 
                    @click="saveEdit(measurement)" 
                    class="btn-save-header" 
                    title="Sauvegarder"
                  >
                    ✓
                  </button>
                  <button 
                    v-if="editingId === measurement.id" 
                    @click="cancelEdit" 
                    class="btn-cancel-header" 
                    title="Annuler"
                  >
                    ✕
                  </button>
                  <button 
                    v-if="editingId !== measurement.id" 
                    @click="startEdit(measurement)" 
                    class="btn-edit-header" 
                    title="Modifier"
                  >
                    ✏️
                  </button>
                  <button 
                    v-if="editingId !== measurement.id" 
                    @click="deleteMeasurement(measurement)" 
                    class="btn-delete-header" 
                    title="Supprimer"
                  >
                    🗑️
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="metric in allMetrics" 
              :key="metric.key"
              class="metric-row"
            >
              <td class="metric-label">
                <div class="metric-info">
                  <span class="metric-name">{{ metric.label }}</span>
                  <div class="tooltip-container">
                    <span class="info-icon">ℹ️</span>
                    <div class="custom-tooltip">
                      {{ metric.description }}
                    </div>
                  </div>
                </div>
              </td>
              
              <!-- Cellules de données pour chaque mesure -->
              <td 
                v-for="measurement in measurements" 
                :key="`${metric.key}-${measurement.id}`"
                :class="getStatusClass(measurement.data[metric.key]?.status)"
                class="metric-cell"
              >
                <div v-if="editingId === measurement.id && editData[metric.key]" class="edit-input">
                  <div class="value-input-group">
                    <input 
                      v-model.number="editData[metric.key].value"
                      type="number"
                      :step="metric.key === 'poids' ? '0.001' : '0.1'"
                      class="inline-input"
                      @blur="validateEditValue(metric.key)"
                    />
                  </div>
                  <select 
                    v-model="editData[metric.key].status"
                    class="inline-status-select"
                    :class="getStatusSelectClass(editData[metric.key]?.status)"
                  >
                    <option value="faible">🔵 Faible</option>
                    <option value="standard">🟢 Standard</option>
                    <option value="excellent">🟢 Excellent</option>
                    <option value="eleve">🟡 Élevé</option>
                    <option value="trop-eleve">🔴 Trop élevé</option>
                  </select>
                </div>
                <div v-else class="metric-value">
                  <span class="value">{{ formatValue(measurement.data[metric.key]?.value, metric.key) }}</span>
                  <span class="unit" v-if="metric.unit && measurement.data[metric.key]?.value !== null && measurement.data[metric.key]?.value !== undefined && measurement.data[metric.key]?.value !== ''">({{ metric.unit }})</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal de confirmation de suppression -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content" @click.stop>
        <h3>Confirmer la suppression</h3>
        <p>Êtes-vous sûr de vouloir supprimer cette mesure du {{ measurementToDelete?.date }} à {{ measurementToDelete?.time }} ?</p>
        <div class="modal-actions">
          <button @click="closeDeleteModal" class="btn-cancel">Annuler</button>
          <button @click="confirmDelete" class="btn-confirm">Supprimer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'

export default {
  name: 'DataTable',
  props: {
    measurements: {
      type: Array,
      required: true
    }
  },
  emits: ['measurement-updated', 'measurement-deleted'],
  setup(props, { emit }) {
    // Toutes les métriques avec leurs labels et descriptions pour l'affichage vertical
    const allMetrics = ref([
      { key: 'poids', label: 'Poids', unit: 'kg', description: 'Masse corporelle totale mesurée en kilogrammes' },
      { key: 'imc', label: 'IMC', unit: '', description: 'Indice de masse corporelle, norme internationale : IMC = poids corporel (kg) / taille (m) au carré' },
      { key: 'graisse_corporelle', label: 'Graisse Corporelle', unit: '%', description: 'Pourcentage de graisse dans le corps par rapport au poids total' },
      { key: 'frequence_cardiaque', label: 'Fréquence Cardiaque', unit: 'BPM', description: 'Nombre de battements de cœur par minute au repos' },
      { key: 'indice_cardiaque', label: 'Indice Cardiaque', unit: '', description: 'Indicateur de la condition cardiovasculaire basé sur la fréquence cardiaque' },
      { key: 'taux_musculaire', label: 'Taux Musculaire', unit: '%', description: 'Pourcentage de muscle dans le corps par rapport au poids total' },
      { key: 'poids_sans_graisse', label: 'Poids sans Graisse', unit: 'kg', description: 'Poids corporel excluant la masse graisseuse (muscles, os, organes, eau)' },
      { key: 'graisse_sous_cutanee', label: 'Graisse Sous-cutanée', unit: '%', description: 'Graisse située directement sous la peau, visible et palpable' },
      { key: 'graisse_viscerale', label: 'Graisse Viscérale', unit: '', description: 'Graisse autour des organes internes, facteur de risque cardiovasculaire' },
      { key: 'eau_corporelle', label: 'Eau Corporelle', unit: '%', description: 'Pourcentage d\'eau dans le corps, essentielle pour tous les processus vitaux' },
      { key: 'muscle_squelettique', label: 'Muscle Squelettique', unit: '%', description: 'Muscles attachés au squelette, responsables des mouvements volontaires' },
      { key: 'masse_musculaire', label: 'Masse Musculaire', unit: 'kg', description: 'Poids total de tous les muscles du corps en kilogrammes' },
      { key: 'masse_osseuse', label: 'Masse Osseuse', unit: 'kg', description: 'Poids du squelette, indicateur de la densité et santé osseuse' },
      { key: 'proteine', label: 'Protéine', unit: '%', description: 'Pourcentage de protéines dans le corps, composant essentiel des muscles et organes' },
      { key: 'taux_metabolique_basal', label: 'Taux Métabolique Basal', unit: 'kcal', description: 'Nombre de calories brûlées par jour au repos pour maintenir les fonctions vitales' },
      { key: 'age_corporel', label: 'Âge Corporel', unit: 'ans', description: 'Âge physiologique estimé du corps basé sur la composition corporelle' }
    ])

    // État d'édition
    const editingId = ref(null)
    const editData = reactive({})

    // Modal de suppression
    const showDeleteModal = ref(false)
    const measurementToDelete = ref(null)

    // Formatage des valeurs
    const formatValue = (value, key) => {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'number') {
        // Le poids affiche 2 décimales, les autres 1 décimale
        return key === 'poids' ? value.toFixed(2) : value.toFixed(1)
      }
      return value
    }

    // Classes CSS pour les statuts
    const getStatusClass = (status) => {
      return {
        'status-faible': status === 'faible',
        'status-standard': status === 'standard',
        'status-excellent': status === 'excellent',
        'status-eleve': status === 'eleve',
        'status-trop-eleve': status === 'trop-eleve'
      }
    }

    // Classes CSS pour les sélecteurs de statut
    const getStatusSelectClass = (status) => {
      return {
        'select-faible': status === 'faible',
        'select-standard': status === 'standard',
        'select-excellent': status === 'excellent',
        'select-eleve': status === 'eleve',
        'select-trop-eleve': status === 'trop-eleve'
      }
    }

    // Démarrer l'édition
    const startEdit = (measurement) => {
      editingId.value = measurement.id
      
      // Copier les données pour l'édition
      allMetrics.value.forEach(metric => {
        const key = metric.key
        editData[key] = {
          value: measurement.data[key]?.value || '',
          unit: measurement.data[key]?.unit || metric.unit,
          status: measurement.data[key]?.status || 'standard'
        }
      })
    }

    // Annuler l'édition
    const cancelEdit = () => {
      editingId.value = null
      Object.keys(editData).forEach(key => delete editData[key])
    }

    // Valider une valeur éditée
    const validateEditValue = (key) => {
      const value = editData[key].value
      if (value === '' || value === null || value === undefined) return

      // Calculer le nouveau statut (logique simplifiée)
      editData[key].status = calculateStatus(key, value)
    }

    // Calculer le statut (même logique que dans DataForm)
    const calculateStatus = (key, value) => {
      const ranges = {
        poids: { faible: [0, 50], standard: [50, 90], eleve: [90, 110], trop_eleve: [110, 999] },
        imc: { faible: [0, 18.5], standard: [18.5, 25], eleve: [25, 30], trop_eleve: [30, 999] },
        graisse_corporelle: { faible: [0, 10], standard: [10, 20], eleve: [20, 30], trop_eleve: [30, 999] },
        frequence_cardiaque: { faible: [0, 60], standard: [60, 100], eleve: [100, 120], trop_eleve: [120, 999] },
        graisse_viscerale: { faible: [0, 5], standard: [5, 10], eleve: [10, 15], trop_eleve: [15, 999] },
        eau_corporelle: { faible: [0, 50], standard: [50, 65], eleve: [65, 75], trop_eleve: [75, 999] }
      }

      const range = ranges[key]
      if (!range) return 'standard'

      if (value <= range.faible[1]) return 'faible'
      if (value <= range.standard[1]) return 'standard'
      if (value <= range.eleve[1]) return 'eleve'
      return 'trop-eleve'
    }

    // Sauvegarder l'édition
    const saveEdit = (measurement) => {
      const updatedMeasurement = {
        ...measurement,
        data: {
          ...measurement.data
        }
      }

      // Mettre à jour les données modifiées
      allMetrics.value.forEach(metric => {
        const key = metric.key
        if (editData[key]) {
          updatedMeasurement.data[key] = { ...editData[key] }
        }
      })

      emit('measurement-updated', updatedMeasurement)
      cancelEdit()
    }

    // Supprimer une mesure
    const deleteMeasurement = (measurement) => {
      measurementToDelete.value = measurement
      showDeleteModal.value = true
    }

    // Confirmer la suppression
    const confirmDelete = () => {
      if (measurementToDelete.value) {
        emit('measurement-deleted', measurementToDelete.value.id)
      }
      closeDeleteModal()
    }

    // Fermer le modal de suppression
    const closeDeleteModal = () => {
      showDeleteModal.value = false
      measurementToDelete.value = null
    }

    return {
      allMetrics,
      editingId,
      editData,
      showDeleteModal,
      measurementToDelete,
      formatValue,
      getStatusClass,
      getStatusSelectClass,
      startEdit,
      cancelEdit,
      validateEditValue,
      saveEdit,
      deleteMeasurement,
      confirmDelete,
      closeDeleteModal
    }
  }
}
</script>

<style scoped>
.data-table {
  width: 100%;
}

.no-data {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  font-size: 1.1rem;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.table-header h3 {
  color: #2c3e50;
  margin: 0;
}

.legend {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-faible { background-color: #cce7ff; }
.status-standard { background-color: #d4edda; }
.status-excellent { background-color: #c3e6cb; }
.status-eleve { background-color: #fff3cd; }
.status-trop-eleve { background-color: #f8d7da; }

.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.measurements-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  min-width: 800px;
}

/* Headers pour la nouvelle structure verticale */
.metric-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  border: 1px solid #dee2e6;
  width: 200px;
  min-width: 200px;
}

.date-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem 0.75rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
}

/* Structure verticale - Headers */
.date-header .datetime {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.date-header .date {
  font-weight: 600;
  color: white;
  font-size: 0.9rem;
}

.date-header .time {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
}

.header-actions {
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}

.btn-save-header, .btn-cancel-header, .btn-edit-header, .btn-delete-header {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s ease;
}

.btn-save-header:hover, .btn-edit-header:hover {
  background: rgba(40, 167, 69, 0.8);
}

.btn-cancel-header:hover, .btn-delete-header:hover {
  background: rgba(220, 53, 69, 0.8);
}

/* Structure verticale - Rows */
.metric-row {
  transition: all 0.3s ease;
}

.metric-row:hover {
  background-color: #f8f9fa;
}

.metric-row:nth-child(even) {
  background-color: #f9f9f9;
}

.metric-label {
  background-color: #f8f9fa;
  border-right: 2px solid #dee2e6;
  padding: 1rem;
  font-weight: 600;
  width: 200px;
  min-width: 200px;
}

.metric-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.info-icon {
  font-size: 0.85rem;
  cursor: help;
  opacity: 0.6;
  transition: all 0.3s ease;
  padding: 2px;
  border-radius: 50%;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-icon:hover {
  opacity: 1;
  background: rgba(102, 126, 234, 0.2);
  transform: scale(1.1);
}

.custom-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: -10px;
  left: 25px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-align: left;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  max-width: 640px;
  white-space: normal;
  width: max-content;
  min-width: 400px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
  z-index: 1000;
  transition: all 0.3s ease;
  transform: translateY(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}


.tooltip-container:hover .custom-tooltip {
  visibility: visible;
  opacity: 1;
  transform: translateY(0);
}

.metric-name {
  color: #2c3e50;
  font-size: 0.95rem;
  font-weight: 600;
}

.metric-unit {
  color: #6c757d;
  font-size: 0.8rem;
  font-weight: normal;
}

.metric-cell {
  min-width: 120px;
  text-align: center;
  padding: 0.75rem;
  border-bottom: 1px solid #e1e8ed;
  vertical-align: middle;
}

.metric-value {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
}

.value {
  font-weight: 600;
  font-size: 0.95rem;
  color: #2c3e50;
}

.unit {
  color: #6c757d;
  font-size: 0.8rem;
  font-weight: normal;
}

.edit-input {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.value-input-group {
  display: flex;
  justify-content: center;
  width: 100%;
}

.inline-input {
  width: 80px;
  padding: 0.5rem;
  border: 2px solid #667eea;
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
}

.inline-input:focus {
  outline: none;
  border-color: #764ba2;
}

.inline-status-select {
  padding: 0.25rem 0.5rem;
  border: 2px solid #e1e8ed;
  border-radius: 4px;
  font-size: 0.8rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
}

.inline-status-select:focus {
  outline: none;
  border-color: #667eea;
}

/* Classes pour les sélecteurs de statut inline */
.select-faible {
  border-color: #17a2b8;
  background-color: #cce7ff;
}

.select-standard {
  border-color: #28a745;
  background-color: #d4edda;
}

.select-excellent {
  border-color: #155724;
  background-color: #c3e6cb;
}

.select-eleve {
  border-color: #ffc107;
  background-color: #fff3cd;
}

.select-trop-eleve {
  border-color: #dc3545;
  background-color: #f8d7da;
}

.actions-cell {
  min-width: 100px;
  text-align: center;
}

.row-actions,
.edit-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.btn-edit,
.btn-delete,
.btn-save,
.btn-cancel {
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-edit {
  background: #e3f2fd;
  color: #1976d2;
}

.btn-edit:hover {
  background: #bbdefb;
  transform: translateY(-1px);
}

.btn-delete {
  background: #ffebee;
  color: #d32f2f;
}

.btn-delete:hover {
  background: #ffcdd2;
  transform: translateY(-1px);
}

.btn-save {
  background: #e8f5e8;
  color: #2e7d32;
}

.btn-save:hover {
  background: #c8e6c9;
  transform: translateY(-1px);
}

.btn-cancel {
  background: #fafafa;
  color: #757575;
}

.btn-cancel:hover {
  background: #eeeeee;
  transform: translateY(-1px);
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-content h3 {
  margin-bottom: 1rem;
  color: #2c3e50;
}

.modal-content p {
  margin-bottom: 1.5rem;
  color: #6c757d;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.modal-actions .btn-cancel,
.modal-actions .btn-confirm {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-actions .btn-cancel {
  background: #e1e8ed;
  color: #2c3e50;
}

.modal-actions .btn-cancel:hover {
  background: #d1d9e0;
}

.modal-actions .btn-confirm {
  background: #dc3545;
  color: white;
}

.modal-actions .btn-confirm:hover {
  background: #c82333;
  transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .legend {
    justify-content: flex-start;
  }
  
  .measurements-table {
    font-size: 0.9rem;
  }
  
  .measurements-table th,
  .measurements-table td {
    padding: 0.75rem 0.5rem;
  }
  
  .inline-status-select {
    min-width: 100px;
    font-size: 0.75rem;
  }
  
  .edit-input {
    gap: 0.25rem;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .modal-actions .btn-cancel,
  .modal-actions .btn-confirm {
    width: 100%;
  }
}
</style>
